import { Component, OnInit, ViewChild } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { CustomTableComponent } from 'src/app/components/custom-table/custom-table.component';
import { StepPanelEnums } from 'src/app/enum/step-panel-enums';
import { RemissionModel } from 'src/app/models/rental/remission.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { MassiveOrderDetailSelectionTable } from 'src/app/models/tables/preliminaries/massive-order-detail-selection-table.model';
import { MassiveOrderSelectionTableModel } from 'src/app/models/tables/preliminaries/massive-order-selection-table.model';
import { RemissionFromOrderService } from 'src/app/services/rental/remission-from-order.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-massive-order',
  templateUrl: './massive-order.component.html',
  styleUrls: ['./massive-order.component.scss'],
  standalone: false,
})
export class MassiveOrderComponent implements OnInit {

  @ViewChild('tableDetail') tableDetail!: CustomTableComponent;
  remissionId: number = 0;
  orderId: number = 0;
  ascendant: boolean = true;
  ascendantDetail: boolean = true;
  columnSort: string | null = null;
  columnSortDetail: string | null = null;
  currentStep: number = 1;
  data: PagedResponseModel<MassiveOrderSelectionTableModel> =
    new PagedResponseModel<MassiveOrderSelectionTableModel>();
  dataDetail: PagedResponseModel<MassiveOrderDetailSelectionTable> =
    new PagedResponseModel<MassiveOrderDetailSelectionTable>();
  filters: ColumnResponseModel[] = [];
  filtersDetail: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  headerDetail: ColumnResponseModel[] = [];
  limit: number = 10;
  limitDetail: number = 100;
  loading: boolean = false;
  page: number = 1;
  pageDetail: number = 1;
  remissionModel: RemissionModel = new RemissionModel();
  readonly stepPanel = StepPanelEnums;
  subscription: Subscription;

  constructor(public config: DynamicDialogConfig,
    private remissionFromOrderService: RemissionFromOrderService,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.remissionId = inputs.remissionId;
    });
    this.loading = true;
    await this.getDataForStep(StepPanelEnums.PanelOne);
    setTimeout(() => this.loading = false, 300);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async getColumnsForMassiveOrderSelection() {
    try {
      if (this.header.length === 0) {
        const response = await firstValueFrom(this.remissionFromOrderService.getColumnsForMassiveOrderSelection());
        this.header = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para seleccionar el pedido.', error);
    }
  }

  async getColumnsForMassiveOrderDetailSelection() {
    try {
      if (this.headerDetail.length === 0) {
        const response = await firstValueFrom(this.remissionFromOrderService.getColumnsForMassiveOrderDetailSelection());
        this.headerDetail = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para seleccionar artículos del pedido.', error);
    }
  }

  async getDataForStep(step: StepPanelEnums) {
    try {
      const actions: Record<number, () => Promise<any>> = {
        [StepPanelEnums.PanelOne]: () => Promise.all([
          this.getColumnsForMassiveOrderSelection(),
          this.getMassiveOrderForSelection()
        ]),
        [StepPanelEnums.PanelTwo]: () => Promise.all([
          this.getColumnsForMassiveOrderDetailSelection(),
          this.getMassiveOrderDetailForSelection()
        ])
      };
      if (actions[step]) {
        await actions[step]();
      }
    } catch (error) {
      console.error(`Error cargando datos del ${StepPanelEnums[step]}:`, error);
    }
  }

  async getMassiveOrderForSelection() {
    try {
      const response = await firstValueFrom(this.remissionFromOrderService.getMassiveOrderForSelection(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters, this.remissionId
      ));
      this.data = response;
    } catch (error) {
      console.error('Error al obtener los artículos del pedido.', error);
    }
  }

  async getMassiveOrderDetailForSelection() {
    try {
      const response = await firstValueFrom(this.remissionFromOrderService.getMassiveOrderDetailForSelection(
        this.pageDetail, this.limitDetail, this.columnSortDetail, this.ascendantDetail, this.filtersDetail, this.orderId
      ));
      this.dataDetail = response;
    } catch (error) {
      console.error('Error al obtener los artículos del pedido.', error);
    }
  }

  async goToStep(step: StepPanelEnums, activateCallback: (step: number) => void) {
    if (step > StepPanelEnums.PanelOne && this.orderId === 0) {
      this.toastHandlerService.showToast('warning', 'Por favor, seleccione un pedido válido.');
      return;
    }
    if (step === StepPanelEnums.PanelOne) {
      this.orderId = 0;
    }
    await this.getDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
    if (step === StepPanelEnums.PanelTwo) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.interceptChildTableAction()
        }, 100);
      });
    }
  }

  interceptChildTableAction() {
    if (this.tableDetail) {
      //Intercepta el botón "Agregar" para mostrar confirmación antes de ejecutar la acción:
      const originalReturnSelected = this.tableDetail.returnSelectedData.bind(this.tableDetail);
      this.tableDetail.returnSelectedData = async () => {
        const result = await Swal.fire({
          title: 'Atención',
          text: "Los artículos serán despachados desde la bodega principal. ¿Realmente desea continuar?",
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Si',
          cancelButtonText: 'No'
        });
        if (result.isConfirmed) {
          originalReturnSelected();
        }
      };
      //Sobrescribe "Recuperar" para evitar que se limpien los checks:
      this.tableDetail.recoverData = () => {
        this.onPageChange({
          page: 1,
          pageSize: this.tableDetail.data.pageSize,
          sortField: this.tableDetail.previousSort?.field,
          sortOrder: this.tableDetail.previousSort?.isAscsendant || 1,
        });
        this.tableDetail.filtersChanged = false;
      };
    }
  }

  async onFilterChange(event: { filters: any[] }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    const cleanFilters = this.removeDuplicates(filters);
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.filters = cleanFilters;
        this.filters.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelOne));
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.filtersDetail = cleanFilters;
        this.filtersDetail.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelTwo));
        break;
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.page = event.page;
        this.limit = event.pageSize;
        this.columnSort = event.sortField || null;
        this.ascendant = event.sortOrder === 1;
        await this.getMassiveOrderForSelection();
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.pageDetail = event.page;
        this.limitDetail = event.pageSize;
        this.columnSortDetail = event.sortField || null;
        this.ascendantDetail = event.sortOrder === 1;
        await this.getMassiveOrderDetailForSelection();
        break;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  processFilter(filter: any, step: StepPanelEnums) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    const isInvalid = valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0;
    if (isInvalid) {
      if (step == StepPanelEnums.PanelOne) {
        this.filters = this.filters.filter(f => f.propertyName !== propertyName);
      } else if (step == StepPanelEnums.PanelTwo) {
        this.filtersDetail = this.filtersDetail.filter(f => f.propertyName !== propertyName);
      }
    } else {
      const list = step === StepPanelEnums.PanelOne ? this.filters : this.filtersDetail;
      const existingFilter = list.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  selectionChange({ selectedItem }: { selectedItem?: MassiveOrderSelectionTableModel }) {
    const { orderId = 0 } = selectedItem ?? {};
    this.orderId = orderId;
  }
}
